import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Upload, X, Check, User, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/api';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

const SERVICES = [
  { key: 'MECANIQUE_MOTO', label: '🏍️ Mécanique Moto' },
  { key: 'MECANIQUE_VOITURE', label: '🚗 Mécanique Voiture' },
  { key: 'VULCANISATEUR', label: '🔩 Vulcanisateur' },
  { key: 'ELECTRICITE_AUTO', label: '⚡ Électricité Auto' },
  { key: 'CARROSSERIE', label: '🎨 Carrosserie' },
  { key: 'CLIMATISATION', label: '❄️ Climatisation' },
  { key: 'VIDANGE_ENTRETIEN', label: '🔧 Vidange & Entretien' },
  { key: 'PIECES_DETACHEES', label: '⚙️ Pièces détachées' },
];

const PAYMENTS = [
  { key: 'MOBILE_MONEY', label: 'Mobile Money (MTN)', color: '#FFC107', bg: '#FFFBEA', logo: '/mtn-momo.png' },
  { key: 'MOOV_MONEY', label: 'Moov Money', color: '#2196F3', bg: '#EAF4FF', logo: '/moov-money.png' },
  { key: 'CELTIS_CASH', label: 'Celtiis Cash', color: '#1A5276', bg: '#EAF0FB', logo: '/celtiis-cash.png' },
];

const CITIES = ['Cotonou', 'Porto-Novo'];

export default function RegisterPage() {
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get('type') === 'prestataire' ? 'prestataire' : 'client');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [realisationPhotos, setRealisationPhotos] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState(['MOBILE_MONEY']);
  const [errors, setErrors] = useState({});
  const [cipPhoto, setCipPhoto] = useState(null);
  const [momoNumbers, setMomoNumbers] = useState({ MTN: '', MOOV: '', CELTIIS: '' });

  const navigate = useNavigate();
  const { login } = useAuthStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [form, setForm] = useState({
    email: '', phone: '', password: '',
    firstName: '', lastName: '',
    city: '', neighborhood: '', address: '',
    shopName: '', bio: '',
    experienceYears: '0', availability: 'Lun-Sam 7h-18h',
    priceMin: '', priceMax: '',
    whatsapp: '', secondPhone: '',
    momoPhone: '', momoNetwork: 'MTN',
    cip: '', ifu: '',
    urgenceNom: '', urgencePhone: '',
  });

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const toggleService = (key) => {
    setSelectedServices(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const togglePayment = (key) => {
    setSelectedPayments(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleProfilePhoto = (e) => {
    const file = e.target.files[0];
    if (file) setProfilePhoto({ file, preview: URL.createObjectURL(file) });
  };

  const handleRealisationPhotos = (e) => {
    const files = Array.from(e.target.files).slice(0, 8 - realisationPhotos.length);
    const newPhotos = files.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setRealisationPhotos(prev => [...prev, ...newPhotos].slice(0, 8));
  };

  const validate = () => {
    const errs = {};
    if (!form.email) errs.email = 'Email requis';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Email invalide';
    if (!form.phone) errs.phone = 'Téléphone requis';
    if (!form.password || form.password.length < 8) errs.password = 'Minimum 8 caractères';
    if (!form.firstName) errs.firstName = 'Prénom requis';
    if (!form.lastName) errs.lastName = 'Nom requis';
    if (!form.city) errs.city = 'Ville requise';
    if (type === 'prestataire') {
      if (!form.neighborhood) errs.neighborhood = 'Quartier requis';
      if (selectedServices.length === 0) errs.services = 'Choisissez au moins un service';
      if (!Object.values(momoNumbers).some(n => n.trim())) errs.momoPhone = "Ajoutez au moins un numéro MoMo";
      if (!cipPhoto) errs.cip = "Photo du CIP requise";
      if (!form.urgenceNom) errs.urgenceNom = 'Contact urgence requis';
      if (!form.urgencePhone) errs.urgencePhone = 'Téléphone urgence requis';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { toast.error('Veuillez corriger les erreurs.'); return; }
    setLoading(true);

    try {
      const payload = {
        email: form.email, phone: form.phone, password: form.password,
        firstName: form.firstName, lastName: form.lastName, city: form.city,
      };

      let data;
      if (type === 'client') {
        payload.neighborhood = form.neighborhood;
        ({ data } = await authService.registerClient(payload));
      } else {
        Object.assign(payload, {
          neighborhood: form.neighborhood, address: form.address,
          shopName: form.shopName, bio: form.bio,
          experienceYears: form.experienceYears, availability: form.availability,
          priceMin: form.priceMin, priceMax: form.priceMax,
          whatsapp: form.whatsapp, secondPhone: form.secondPhone,
          momoPhone: momoNumbers.MTN || momoNumbers.MOOV || momoNumbers.CELTIIS,
          momoNetwork: momoNumbers.MTN ? "MTN" : momoNumbers.MOOV ? "MOOV" : "CELTIIS",
          momoNumbers: JSON.stringify(momoNumbers),
          cip: form.cip, ifu: form.ifu,
          urgenceNom: form.urgenceNom, urgencePhone: form.urgencePhone,
          services: JSON.stringify(selectedServices),
          paymentMethods: JSON.stringify(selectedPayments),
        });
        ({ data } = await authService.registerPrestataire(payload));
      }

      // ── Set le token IMMÉDIATEMENT après inscription ──
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
      useAuthStore.setState({
        user: data.user, accessToken: data.accessToken,
        refreshToken: data.refreshToken, isAuthenticated: true,
      });

      // ── Upload photos en parallèle ──
      const uploadTasks = [];

      if (profilePhoto) {
        const fd = new FormData();
        fd.append('photo', profilePhoto.file);
        const route = type === 'prestataire' ? '/prestataires/photo/profile' : '/users/photo';
        uploadTasks.push(
          api.post(route, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(res => {
              // Mettre à jour la photo dans le store
              const photoUrl = res.data.profilePhotoUrl || res.data.photoUrl;
              if (photoUrl) useAuthStore.setState(s => ({ user: { ...s.user, profilePhotoUrl: photoUrl } }));
            })
            .catch(err => console.warn('Photo profil failed:', err))
        );
      }

      if (type === 'prestataire' && cipPhoto) {
        const fd = new FormData();
        fd.append('photo', cipPhoto);
        uploadTasks.push(
          api.post('/prestataires/photo/cip', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            .catch(err => console.warn('CIP photo failed:', err))
        );
      }

      if (type === 'prestataire' && realisationPhotos.length > 0) {
        realisationPhotos.forEach(photo => {
          const fd = new FormData();
          fd.append('photo', photo.file);
          uploadTasks.push(
            api.post('/prestataires/photo/realisation', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
              .catch(err => console.warn('Réalisation photo failed:', err))
          );
        });
      }

      // Attendre tous les uploads avant de naviguer
      if (uploadTasks.length > 0) {
        toast.loading('Upload des photos...', { id: 'upload' });
        await Promise.allSettled(uploadTasks);
        toast.dismiss('upload');
      }

      toast.success(data.message || 'Compte créé avec succès !');
      navigate(type === 'client' ? '/dashboard' : '/espace-prestataire');
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la création du compte.';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const pwdStrength = () => {
    let s = 0;
    if (form.password.length >= 8) s++;
    if (/[A-Z]/.test(form.password)) s++;
    if (/[0-9]/.test(form.password)) s++;
    if (/[^A-Za-z0-9]/.test(form.password)) s++;
    return s;
  };

  const strength = pwdStrength();
  const strengthColors = ['#EF4444', '#F59E0B', '#10B981', '#10B981'];
  const strengthLabels = ['Faible', 'Moyen', 'Fort', 'Très fort'];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)', paddingTop: '80px' }}>
      {/* HEADER */}
      <div style={{ background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', padding: '3rem 0 2rem' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <span className="section-tag" style={{ background: 'rgba(255,107,0,0.15)', borderColor: 'rgba(255,107,0,0.4)' }}>
            INSCRIPTION GRATUITE
          </span>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            color: 'white', letterSpacing: '2px', lineHeight: 0.95, marginTop: '1rem',
          }}>
            CRÉEZ VOTRE<br /><span style={{ color: 'var(--orange)' }}>COMPTE</span>
          </h1>

          {/* TYPE SELECTOR */}
          <div style={{
            display: 'inline-flex', background: 'rgba(255,255,255,0.08)',
            borderRadius: '12px', padding: '6px', marginTop: '2rem',
            border: '1px solid rgba(255,255,255,0.15)',
          }}>
            {[
              { key: 'client', icon: <User size={20} />, label: 'Je suis Client' },
              { key: 'prestataire', icon: <Wrench size={20} />, label: 'Je suis Prestataire' },
            ].map(t => (
              <button key={t.key} onClick={() => setType(t.key)} style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.75rem 1.8rem',
                background: type === t.key ? 'var(--orange)' : 'transparent',
                border: 'none', borderRadius: '8px', color: 'white',
                fontFamily: 'var(--font-cond)', fontSize: '1rem', fontWeight: 700,
                letterSpacing: '1px', cursor: 'pointer', transition: 'all 0.3s',
                boxShadow: type === t.key ? '0 4px 15px rgba(255,107,0,0.4)' : 'none',
              }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── BANNIÈRE MARKETING PRESTATAIRE ── */}
      <AnimatePresence>
        {type === 'prestataire' && (
          <motion.div
            key="marketing"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{ overflow: 'hidden', background: 'linear-gradient(135deg, #FF6B00 0%, #CC4400 50%, #FF8C00 100%)', position: 'relative' }}
          >
            {/* Particules décoratives */}
            {[...Array(6)].map((_, i) => (
              <motion.div key={i}
                animate={{ y: [0, -20, 0], opacity: [0.15, 0.4, 0.15], rotate: [0, 180, 360] }}
                transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.5 }}
                style={{
                  position: 'absolute',
                  left: `${10 + i * 16}%`, top: `${20 + (i % 3) * 25}%`,
                  fontSize: ['⚙️','🔧','🔩','⚡','🏍️','🚗'][i],
                  fontSize: '1.5rem', opacity: 0.2, pointerEvents: 'none',
                }}
              />
            ))}

            <div className="container" style={{ padding: '3rem 1.5rem', position: 'relative', zIndex: 1 }}>

              {/* Titre principal */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={{ textAlign: 'center', marginBottom: '2.5rem' }}
              >
                <div style={{ display: 'inline-block', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '30px', padding: '0.4rem 1.2rem', fontSize: '0.75rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '3px', color: 'white', marginBottom: '1rem' }}>
                  💰 OPPORTUNITÉ DE REVENUS
                </div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 6vw, 4rem)', color: 'white', letterSpacing: '2px', lineHeight: 0.95, marginBottom: '0.8rem', textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                  GAGNEZ PLUS DE<br />
                  <motion.span
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    style={{ color: '#FFE500', display: 'inline-block', textShadow: '0 0 30px rgba(255,229,0,0.6)' }}
                  >
                    100.000 FCFA
                  </motion.span>
                  <br />PAR MOIS
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.05rem', maxWidth: '500px', margin: '0 auto', lineHeight: 1.6 }}>
                  Rejoignez les <strong>450+ prestataires</strong> qui développent leur activité avec ELIM DÉPANNAGE
                </p>
              </motion.div>

              {/* Stats chocs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
                {[
                  { num: '2 800+', label: 'Clients actifs', icon: '👥', delay: 0.3 },
                  { num: '×3',     label: 'Plus de contacts\navec photos',   icon: '📸', delay: 0.4 },
                  { num: '4.8★',   label: 'Note moyenne\nplateforme', icon: '⭐', delay: 0.5 },
                  { num: '100%',   label: 'Gratuit\npour s\'inscrire',  icon: '🆓', delay: 0.6 },
                ].map((s, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: s.delay }}
                    whileHover={{ scale: 1.05, y: -4 }}
                    style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '14px', padding: '1.2rem', textAlign: 'center' }}
                  >
                    <div style={{ fontSize: '1.8rem', marginBottom: '0.4rem' }}>{s.icon}</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: '#FFE500', letterSpacing: '1px', lineHeight: 1, marginBottom: '0.3rem' }}>{s.num}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1px', whiteSpace: 'pre-line', lineHeight: 1.3 }}>{s.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Arguments */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.8rem' }}>
                {[
                  { icon: '📱', title: 'Clients directs sur votre téléphone', desc: 'Recevez des demandes de clients près de chez vous instantanément sur WhatsApp.' },
                  { icon: '🛡️', title: 'Profil vérifié = confiance maximale', desc: 'Le badge "Vérifié" rassure les clients et vous positionne en tête des résultats.' },
                  { icon: '💳', title: 'Paiement sécurisé garanti', desc: 'MTN, Moov, Celtis — soyez payé rapidement sans risque d\'impayé.' },
                  { icon: '📊', title: 'Visibilité 24h/24 — 7j/7', desc: 'Votre profil est visible même quand vous dormez. Les clients vous trouvent toujours.' },
                  { icon: '🚀', title: 'Démarrez en moins de 5 minutes', desc: 'Créez votre profil maintenant et recevez votre premier contact dès aujourd\'hui.' },
                  { icon: '🏆', title: 'Les meilleurs gagnent plus', desc: 'Bonnes notes = plus de visibilité = plus de clients = plus de revenus. Simple.' },
                ].map((a, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                    style={{ display: 'flex', gap: '0.8rem', background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '12px', padding: '1rem' }}
                  >
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>{a.icon}</div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: 'white', fontSize: '0.9rem', letterSpacing: '0.5px', marginBottom: '0.2rem' }}>{a.title}</div>
                      <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{a.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA flèche vers le formulaire */}
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                style={{ textAlign: 'center', marginTop: '2rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '2px' }}
              >
                ↓ REMPLISSEZ LE FORMULAIRE CI-DESSOUS ↓
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FORM */}
      <div className="container" style={{ maxWidth: '800px', padding: '2.5rem 1.5rem 5rem' }}>
        <motion.form
          key={type}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={handleSubmit}
          style={{
            background: 'white', borderRadius: '20px',
            padding: 'clamp(1.5rem, 4vw, 3rem)',
            boxShadow: '0 20px 60px rgba(26,59,93,0.12)',
          }}
        >
          {/* ── INFORMATIONS PERSONNELLES ── */}
          <SectionTitle>Informations personnelles</SectionTitle>
          <div className="grid-2">
            <Field label="Prénom *" error={errors.firstName}>
              <input className={`form-input${errors.firstName ? ' error' : ''}`} value={form.firstName} onChange={set('firstName')} placeholder="Votre prénom" />
            </Field>
            <Field label="Nom *" error={errors.lastName}>
              <input className={`form-input${errors.lastName ? ' error' : ''}`} value={form.lastName} onChange={set('lastName')} placeholder="Votre nom" />
            </Field>
            <Field label="Email *" error={errors.email}>
              <input className={`form-input${errors.email ? ' error' : ''}`} type="email" value={form.email} onChange={set('email')} placeholder="votre@email.com" />
            </Field>
            <Field label="Téléphone *" error={errors.phone}>
              <input className={`form-input${errors.phone ? ' error' : ''}`} type="tel" value={form.phone} onChange={set('phone')} placeholder="+229 XX XX XX XX" />
            </Field>
            {type === 'prestataire' && <>
              <Field label="WhatsApp (si différent)">
                <input className="form-input" type="tel" value={form.whatsapp} onChange={set('whatsapp')} placeholder="+229 XX XX XX XX" />
              </Field>
              <Field label="2ème téléphone">
                <input className="form-input" type="tel" value={form.secondPhone} onChange={set('secondPhone')} placeholder="+229 XX XX XX XX" />
              </Field>
            </>}
          </div>

          {/* ── PHOTO DE PROFIL ── */}
          <SectionTitle>Photo de profil</SectionTitle>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{
              width: '100px', height: '100px', borderRadius: '50%', overflow: 'hidden',
              background: 'var(--gray-light)', border: '3px solid var(--gray-mid)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {profilePhoto ? (
                <img src={profilePhoto.preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '2.5rem' }}>👤</span>
              )}
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.75rem 1.5rem', background: 'var(--blue-dark)',
              color: 'white', borderRadius: '8px', cursor: 'pointer',
              fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.9rem',
              letterSpacing: '1px', transition: 'background 0.2s',
            }}>
              <Upload size={16} /> Choisir une photo
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfilePhoto} />
            </label>
            {profilePhoto && (
              <button type="button" onClick={() => setProfilePhoto(null)}
                style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                <X size={14} /> Supprimer
              </button>
            )}
          </div>

          {/* ── PRESTATAIRE SPECIFIC ── */}
          {type === 'prestataire' && (
            <>
              <SectionTitle>Informations professionnelles</SectionTitle>
              <div className="grid-2">
                <Field label="Nom de votre atelier / garage" style={{ gridColumn: 'span 2' }}>
                  <input className="form-input" value={form.shopName} onChange={set('shopName')} placeholder="Ex: Garage Mensah, Auto-Express..." />
                </Field>
                <Field label="Années d'expérience">
                  <select className="form-select" value={form.experienceYears} onChange={set('experienceYears')}>
                    <option value="0">Moins d'1 an</option>
                    <option value="2">1 à 3 ans</option>
                    <option value="4">3 à 5 ans</option>
                    <option value="7">5 à 10 ans</option>
                    <option value="15">Plus de 10 ans</option>
                  </select>
                </Field>
                <Field label="Disponibilité">
                  <select className="form-select" value={form.availability} onChange={set('availability')}>
                    <option value="Lun-Sam 7h-18h">Lun-Sam (7h-18h)</option>
                    <option value="Tous les jours">Tous les jours</option>
                    <option value="24h/24 - 7j/7">24h/24 — 7j/7</option>
                    <option value="Sur rendez-vous">Sur rendez-vous</option>
                  </select>
                </Field>
              </div>

              <Field label="Description de vos services *">
                <textarea className="form-textarea" value={form.bio} onChange={set('bio')}
                  placeholder="Décrivez vos compétences, vos marques de prédilection, vos certifications, ce qui vous différencie..."
                  style={{ minHeight: '120px' }} />
              </Field>

              {/* SERVICES */}
              <Field label="Vos spécialités * (sélectionnez tout ce qui s'applique)" error={errors.services}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '0.3rem' }}>
                  {SERVICES.map(s => (
                    <button key={s.key} type="button" onClick={() => toggleService(s.key)}
                      style={{
                        padding: '0.55rem 1.1rem', borderRadius: '30px',
                        border: `2px solid ${selectedServices.includes(s.key) ? 'var(--orange)' : 'var(--gray-mid)'}`,
                        background: selectedServices.includes(s.key) ? 'rgba(255,107,0,0.08)' : 'white',
                        color: selectedServices.includes(s.key) ? 'var(--orange)' : 'var(--gray-text)',
                        fontFamily: 'var(--font-cond)', fontSize: '0.85rem', fontWeight: 700,
                        cursor: 'pointer', transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', gap: '0.3rem',
                      }}>
                      {selectedServices.includes(s.key) && <Check size={12} />}
                      {s.label}
                    </button>
                  ))}
                </div>
                {errors.services && <div className="form-error">{errors.services}</div>}
              </Field>

              {/* PRICING */}
              <div className="grid-2">
                <Field label="Tarif minimum (FCFA)">
                  <input className="form-input" type="number" value={form.priceMin} onChange={set('priceMin')} placeholder="Ex: 2000" />
                </Field>
                <Field label="Tarif maximum (FCFA)">
                  <input className="form-input" type="number" value={form.priceMax} onChange={set('priceMax')} placeholder="Ex: 100000" />
                </Field>
              </div>

              {/* PAYMENT METHODS */}
              <SectionTitle>Moyens de paiement acceptés</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.8rem' }}>
                {PAYMENTS.map(p => {
                  const checked = selectedPayments.includes(p.key);
                  return (
                  <label key={p.key} style={{
                    display: 'flex', alignItems: 'center', gap: '0.8rem',
                    padding: '0.8rem 1rem',
                    border: `2px solid ${checked ? p.color : 'var(--gray-mid)'}`,
                    borderRadius: '10px', cursor: 'pointer',
                    background: checked ? p.bg : 'white',
                    transition: 'all 0.2s',
                    boxShadow: checked ? `0 2px 8px ${p.color}40` : 'none',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => togglePayment(p.key)}
                      style={{ accentColor: p.color, width: '18px', height: '18px', flexShrink: 0 }} />
                    <img src={p.logo} alt={p.label} style={{ width: 36, height: 28, objectFit: 'contain', borderRadius: '4px' }} />
                    <span style={{ fontFamily: 'var(--font-cond)', fontSize: '0.85rem', fontWeight: 700, color: checked ? p.color : 'var(--blue-dark)' }}>
                      {p.label}
                    </span>
                  </label>
                  );
                })}
              </div>


              {/* PAIEMENT MOMO */}
              <SectionTitle>💳 Réception des paiements (obligatoire)</SectionTitle>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-text)', marginBottom: '1rem' }}>
                Renseignez vos numéros Mobile Money pour recevoir vos paiements. Vous pouvez en ajouter plusieurs.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginBottom: '0.5rem' }}>
                {[
                  { key: 'MTN', label: 'MTN Mobile Money', color: '#FFC107', logo: '/mtn-momo.png', payKey: 'MOBILE_MONEY' },
                  { key: 'MOOV', label: 'Moov Money', color: '#2196F3', logo: '/moov-money.png', payKey: 'MOOV_MONEY' },
                  { key: 'CELTIIS', label: 'Celtiis Cash', color: '#1A4A8A', logo: '/celtiis-cash.png', payKey: 'CELTIS_CASH' },
                ].filter(({ payKey }) => selectedPayments.includes(payKey)).map(({ key, label, color, logo }) => (
                  <div key={key} style={{ border: `2px solid ${momoNumbers[key] ? color : 'var(--gray-mid)'}`, borderRadius: '10px', padding: '0.8rem 1rem', background: momoNumbers[key] ? `${color}15` : 'white', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.5rem' }}>
                      <img src={logo} alt={label} style={{ width: 48, height: 36, objectFit: 'contain', borderRadius: '6px' }} />
                      <span style={{ fontWeight: 700, color: 'var(--blue-dark)', fontSize: '0.9rem' }}>{label}</span>
                    </div>
                    <input
                      className="form-input"
                      type="tel"
                      value={momoNumbers[key]}
                      onChange={e => setMomoNumbers(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder="+229 XX XX XX XX (optionnel)"
                      style={{ borderColor: momoNumbers[key] ? color : undefined }}
                    />
                  </div>
                ))}
              </div>
              {errors.momoPhone && <div className="form-error">{errors.momoPhone}</div>}

              {/* DOCUMENTS OFFICIELS */}
              <SectionTitle>📋 Documents officiels</SectionTitle>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: 'var(--blue-dark)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Photo du CIP * <span style={{ color: 'var(--gray-text)', fontWeight: 400, textTransform: 'none' }}>(carte d'identité professionnelle)</span>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `2px dashed ${cipPhoto ? 'var(--orange)' : errors.cip ? 'var(--error)' : 'var(--gray-mid)'}`, borderRadius: '10px', padding: '1.5rem', cursor: 'pointer', background: cipPhoto ? 'rgba(255,107,0,0.04)' : 'var(--gray-light)', transition: 'all 0.2s' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) setCipPhoto(e.target.files[0]); }} />
                  {cipPhoto ? (
                    <div style={{ textAlign: 'center' }}>
                      <img src={URL.createObjectURL(cipPhoto)} alt="CIP" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: '8px', marginBottom: '0.5rem' }} />
                      <div style={{ fontSize: '0.8rem', color: 'var(--orange)', fontWeight: 600 }}>✅ {cipPhoto.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-text)', marginTop: '0.2rem' }}>Cliquez pour changer</div>
                    </div>
                  ) : (
                    <>
                      <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</span>
                      <span style={{ fontWeight: 600, color: 'var(--blue-dark)', fontSize: '0.9rem' }}>Cliquez pour ajouter votre CIP</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-text)', marginTop: '0.3rem' }}>JPG, PNG — Photo claire et lisible</span>
                    </>
                  )}
                </label>
                {errors.cip && <div className="form-error">{errors.cip}</div>}
              </div>
              <Field label="Numéro IFU (optionnel)">
                <input className="form-input"
                  value={form.ifu} onChange={set('ifu')} placeholder="Ex: 123456789" />
              </Field>

              {/* CONTACT URGENCE */}
              <SectionTitle>🆘 Contact d'urgence</SectionTitle>
              <div className="grid-2">
                <Field label="Nom & Prénom *" error={errors.urgenceNom}>
                  <input className={`form-input${errors.urgenceNom ? ' error' : ''}`}
                    value={form.urgenceNom} onChange={set('urgenceNom')} placeholder="Ex: Marie Koffi" />
                </Field>
                <Field label="Téléphone *" error={errors.urgencePhone}>
                  <input className={`form-input${errors.urgencePhone ? ' error' : ''}`} type="tel"
                    value={form.urgencePhone} onChange={set('urgencePhone')} placeholder="+229 XX XX XX XX" />
                </Field>
              </div>

              {/* REALISATION PHOTOS */}
              <SectionTitle>Photos de vos réalisations (max 8)</SectionTitle>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-text)', marginBottom: '1rem' }}>
                Montrez vos meilleurs travaux ! Les clients avec photos reçoivent 3x plus de contacts.
              </p>
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                border: '2px dashed var(--gray-mid)', borderRadius: '12px',
                padding: '2rem', cursor: 'pointer', background: 'var(--gray-light)',
                transition: 'all 0.2s', marginBottom: '1rem',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.background = 'rgba(255,107,0,0.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-mid)'; e.currentTarget.style.background = 'var(--gray-light)'; }}
              >
                <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleRealisationPhotos} />
                <Upload size={32} style={{ color: 'var(--blue-dark)', marginBottom: '0.8rem' }} />
                <strong style={{ color: 'var(--blue-dark)', fontFamily: 'var(--font-cond)', letterSpacing: '1px' }}>
                  AJOUTER DES PHOTOS
                </strong>
                <span style={{ fontSize: '0.82rem', color: 'var(--gray-text)', marginTop: '0.3rem' }}>
                  JPG, PNG — Max 8 Mo par photo — {realisationPhotos.length}/8 photos
                </span>
              </label>

              {realisationPhotos.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.6rem' }}>
                  {realisationPhotos.map((photo, i) => (
                    <div key={i} style={{ position: 'relative', aspectRatio: '1' }}>
                      <img src={photo.preview} alt={`Réalisation ${i + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                      <button type="button"
                        onClick={() => setRealisationPhotos(prev => prev.filter((_, idx) => idx !== i))}
                        style={{
                          position: 'absolute', top: '4px', right: '4px',
                          background: 'rgba(239,68,68,0.9)', border: 'none', color: 'white',
                          width: '20px', height: '20px', borderRadius: '50%', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px',
                        }}>
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── LOCALISATION ── */}
          <SectionTitle>Localisation</SectionTitle>
          <div className="grid-2">
            <Field label="Ville *" error={errors.city}>
              <select className={`form-select${errors.city ? ' error' : ''}`} value={form.city} onChange={set('city')}>
                <option value="">Choisir votre ville</option>
                {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={type === 'prestataire' ? 'Quartier *' : 'Quartier'} error={errors.neighborhood}>
              <input className={`form-input${errors.neighborhood ? ' error' : ''}`} value={form.neighborhood} onChange={set('neighborhood')} placeholder="Votre quartier" />
            </Field>
            {type === 'prestataire' && (
              <Field label="Adresse / Repère" style={{ gridColumn: 'span 2' }}>
                <input className="form-input" value={form.address} onChange={set('address')} placeholder="Rue, carrefour, repère identifiable..." />
              </Field>
            )}
          </div>

          {/* ── SÉCURITÉ ── */}
          <SectionTitle>Sécurité</SectionTitle>
          <div className="grid-2">
            <Field label="Mot de passe *" error={errors.password}>
              <div style={{ position: 'relative' }}>
                <input
                  className={`form-input${errors.password ? ' error' : ''}`}
                  type={showPwd ? 'text' : 'password'}
                  value={form.password} onChange={set('password')}
                  placeholder="Minimum 8 caractères"
                  style={{ paddingRight: '3rem' }}
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  style={{ position: 'absolute', right: '0.8rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-text)' }}>
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.password && (
                <div>
                  <div style={{ height: '4px', background: 'var(--gray-mid)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${strength * 25}%`, height: '100%', background: strengthColors[strength - 1], borderRadius: '2px', transition: 'all 0.3s' }} />
                  </div>
                  <div style={{ fontSize: '0.75rem', color: strengthColors[strength - 1], marginTop: '2px' }}>
                    {strengthLabels[strength - 1]}
                  </div>
                </div>
              )}
            </Field>
          </div>

          {/* ── CGU ── */}
          <div style={{ marginTop: '2rem', padding: '1.2rem', background: 'var(--gray-light)', borderRadius: '10px', border: '1px solid var(--gray-mid)' }}>
            <label style={{ display: 'flex', gap: '0.8rem', cursor: 'pointer', alignItems: 'flex-start' }}>
              <input type="checkbox" required style={{ accentColor: 'var(--orange)', width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' }} />
              <span style={{ fontSize: '0.87rem', color: 'var(--gray-text)', lineHeight: 1.6 }}>
                J'accepte les <Link to="/conditions" style={{ color: 'var(--orange)' }}>Conditions d'utilisation</Link> et la <Link to="/confidentialite" style={{ color: 'var(--orange)' }}>Politique de confidentialité</Link> de MécanoBénin.
                {type === 'prestataire' && ' Je certifie que les informations fournies sont exactes.'}
              </span>
            </label>
          </div>

          {/* SUBMIT */}
          <button type="submit" disabled={loading} className="btn btn-primary btn-full btn-lg" style={{ marginTop: '1.5rem', fontSize: '1.1rem' }}>
            {loading ? (
              <><span className="spinner" /> CRÉATION EN COURS...</>
            ) : (
              type === 'client' ? '✅ CRÉER MON COMPTE CLIENT' : '🔧 CRÉER MON PROFIL PRESTATAIRE'
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.9rem', color: 'var(--gray-text)' }}>
            Déjà inscrit ? <Link to="/connexion" style={{ color: 'var(--orange)', fontWeight: 600 }}>Se connecter</Link>
          </div>
        </motion.form>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--font-cond)', fontSize: '0.75rem', fontWeight: 700,
      letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--orange)',
      borderBottom: '2px solid rgba(255,107,0,0.15)', paddingBottom: '0.7rem',
      marginBottom: '1.5rem', marginTop: '2.5rem',
    }}>
      {children}
    </div>
  );
}

function Field({ label, error, children, style }) {
  return (
    <div className="form-group" style={style}>
      <label className="form-label">{label}</label>
      {children}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}
